import React from "react";
import { ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis, ZAxis } from "recharts";
import { startOfDay } from "date-fns";
import styled from "styled-components";

const parseDomain = (data) => [
    0,
    Math.max(
        Math.max.apply(
            null,
            data.map((entry) => entry.value)
        )
    ),
];

const MAX_BUBBLE_SIZE = 17;
const MAX_SPACE_NEEDED = MAX_BUBBLE_SIZE + 10;

const getTimeSpaces = (numberOfPoints) => {
    const hoursOfDay = 24;
    let length = 6;
    if (numberOfPoints > 24 * 2) {
        length = 24 * 2;
    } else if (numberOfPoints > 24) {
        length = 24;
    } else if (numberOfPoints > 12) {
        length = 12;
    }

    const timeSpaces = Array.from({ length }, (_, index) => {
        const hourSplit = hoursOfDay / length;
        let hourStart = Math.floor(index * hourSplit - (hourSplit >= 1 ? hourSplit / 2 : 0));
        if (hourStart < 0) hourStart = 24 + hourStart;
        hourStart = hourStart.toString().padStart(2, "0");
        let hourEnd = Math.floor(index * hourSplit + (hourSplit >= 1 ? hourSplit / 2 : 0));
        if (hourEnd > 24) hourEnd -= 24;
        hourEnd = hourEnd.toString().padStart(2, "0");
        const hour = Math.floor(index * hourSplit)
            .toString()
            .padStart(2, "0");
        const minutesRange = hourSplit > 1 ? "00" : 30;
        const minutes = hourSplit > 0 ? "00" : 30 * (index % 2).toString().padEnd(2, "0");
        return {
            unixValueFromBeginningOfDay: Math.floor(index * (hoursOfDay / length) * 60 * 60),
            stringRepresentation: {
                mid: `${hour}:${minutes}`,
                range: `${hourStart}:${minutesRange} - ${hourEnd}:${minutesRange}`,
            },
        };
    });

    return {
        timeSpaces,
        hourSplit: hoursOfDay / length,
    };
};

const TooltipContainer = styled.div`
    background-color: ${({ theme }) => theme.palette.background.tooltip};
    padding: 0.5rem;
    min-width: 10rem;
    align-items: center;
    flex-direction: column;
    border-radius: 6px;
`;

const TooltipTitle = styled.p`
    margin-top: 0;
    margin-bottom: 0.5rem;
`;

const TooltipContent = styled.p`
    font-size: 12px;
    margin: 0;
    color: ${({ color }) => color};
`;

const WorkBubbleChart = ({ data, width, dataPropertyName }) => {
    const minPointsFitting = width / MAX_SPACE_NEEDED;
    const { timeSpaces, hourSplit } = getTimeSpaces(minPointsFitting);
    const clusteredData =
        data &&
        Array.isArray(data) &&
        data.reduce((acc, dataPoint) => {
            const timestamp = dataPoint.timestamp || new Date(dataPoint.datetime).valueOf() / 1000;
            const startOfDayInSeconds = startOfDay(timestamp * 1000).valueOf() / 1000;
            const timeSpaceForPoint = timeSpaces.find(
                (timeSpace) =>
                    timeSpace.unixValueFromBeginningOfDay +
                        startOfDayInSeconds -
                        (hourSplit / 2) * 60 * 60 <=
                        timestamp &&
                    timeSpace.unixValueFromBeginningOfDay +
                        startOfDayInSeconds +
                        (hourSplit / 2) * 60 * 60 >=
                        timestamp
            );
            if (timeSpaceForPoint) {
                if (!acc[timeSpaceForPoint.stringRepresentation.mid])
                    acc[timeSpaceForPoint.stringRepresentation.mid] = 0;
                acc[timeSpaceForPoint.stringRepresentation.mid] += 1;
            }
            return acc;
        }, {});

    const formattedData = timeSpaces.map((timeSpace) => ({
        hour: timeSpace.stringRepresentation.mid,
        range: timeSpace.stringRepresentation.range,
        value: (clusteredData && clusteredData[timeSpace.stringRepresentation.mid]) || 0,
        index: 0,
    }));

    const domain = parseDomain(formattedData);

    const renderTooltip = (props) => {
        const { active, payload } = props;

        const name = dataPropertyName?.includes("deployment") ? "deployments" : "commits";

        if (active && payload && payload.length) {
            return (
                <TooltipContainer role="status" aria-live="assertive">
                    <TooltipTitle>{`${payload[0].payload.range}`}</TooltipTitle>
                    <TooltipContent>
                        Number of {name}: {payload[0].payload.value}
                    </TooltipContent>
                </TooltipContainer>
            );
        }
        return null;
    };

    return (
        <div style={{ width: "90%" }}>
            <ResponsiveContainer width="100%" height={60}>
                <ScatterChart
                    accessibilityLayer
                    height={60}
                    margin={{
                        top: 15,
                        right: 0,
                        bottom: 0,
                        left: 0,
                    }}
                >
                    <XAxis
                        type="category"
                        dataKey="hour"
                        interval={0}
                        tick={{ fontSize: 10, transform: "translate(0, 5)" }}
                        tickLine={{ transform: "translate(0, -6)" }}
                    />
                    <YAxis type="number" hide dataKey="index" />
                    <ZAxis type="number" dataKey="value" domain={domain} range={[0, 225]} />
                    <Tooltip
                        cursor={{ strokeDasharray: "3 3" }}
                        wrapperStyle={{ zIndex: 100 }}
                        content={renderTooltip}
                    />
                    <Scatter data={formattedData} fill="#8884d8" />
                </ScatterChart>
            </ResponsiveContainer>
        </div>
    );
};

export default WorkBubbleChart;
